import { MetricStrip } from "@/components/metrics/metric-strip";
import {
  DailyExpensesChart,
  type DailyExpensePoint,
} from "@/components/charts/daily-expenses-chart";
import {
  TopCategoriesCard,
  type TopCategory,
} from "./top-categories-card";
import {
  RecentSignupsCard,
  type RecentSignup,
} from "./recent-signups-card";
import {
  RecentExpensesCard,
  type RecentExpense,
} from "./recent-expenses-card";
import {
  FeedbackQueueCard,
  type FeedbackItem,
} from "./feedback-queue-card";
import type { DashboardMetrics } from "@/lib/metrics/dashboard-metrics";
import { formatInr } from "@/lib/format/inr";

export interface DashboardViewProps {
  metrics: DashboardMetrics;
  dailyExpenses: DailyExpensePoint[];
  topCategories: TopCategory[];
  recentSignups: RecentSignup[];
  recentExpenses: RecentExpense[];
  unresolvedFeedback: FeedbackItem[];
  now: Date;
}

function Section({
  title,
  sub,
  children,
}: {
  title: string;
  sub?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-fc-border bg-gradient-to-b from-fc-raised to-fc-overlay p-4">
      <div>
        <div className="text-[13px] font-semibold text-fc-text">{title}</div>
        {sub ? (
          <div className="text-[11.5px] text-fc-text-dim">{sub}</div>
        ) : null}
      </div>
      {children}
    </div>
  );
}

export function DashboardView({
  metrics,
  dailyExpenses,
  topCategories,
  recentSignups,
  recentExpenses,
  unresolvedFeedback,
  now,
}: DashboardViewProps) {
  return (
    <div className="flex flex-col gap-5">
      <MetricStrip metrics={metrics} />

      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2">
          <Section
            title="Expenses per day"
            sub="Last 14 days · count of expense rows"
          >
            <DailyExpensesChart data={dailyExpenses} />
          </Section>
        </div>
        <Section
          title="Top categories"
          sub={`Last 30 days · ${formatInr(topCategories.reduce((s, c) => s + c.amount, 0))}`}
        >
          <TopCategoriesCard
            data={topCategories}
            totalAmount={topCategories.reduce((s, c) => s + c.amount, 0)}
          />
        </Section>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Section title="Recent signups" sub={`Last ${recentSignups.length}`}>
          <RecentSignupsCard data={recentSignups} now={now} />
        </Section>
        <Section title="Recent expenses" sub={`Last ${recentExpenses.length}`}>
          <RecentExpensesCard data={recentExpenses} now={now} />
        </Section>
      </div>

      <Section
        title="Feedback awaiting reply"
        sub={`${metrics.unresolvedFeedback} unresolved · of ${metrics.totalFeedback} total`}
      >
        <FeedbackQueueCard data={unresolvedFeedback} now={now} />
      </Section>
    </div>
  );
}
